import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import CustomButton from "../../pages/CustomButton";
import { getAuthHeaders } from "../../services/useAuth";
import { BASE_URL } from "../../config";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const ProductPriceManager = () => {
  const resolveVisibleInDcr = (product, price) => {
    if (price && typeof price.visible_in_dcr !== "undefined") {
      return !!price.visible_in_dcr;
    }
    if (typeof product?.visible_in_dcr !== "undefined") {
      return !!product.visible_in_dcr;
    }
    return false;
  };

  const [viewMode, setViewMode] = useState("view");
  const [filterDivision, setFilterDivision] = useState("");
  const [filterDate, setFilterDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectAll, setSelectAll] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [priceListData, setPriceListData] = useState([]);
  const [priceHistoryData, setPriceHistoryData] = useState([]);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedToDelete, setSelectedToDelete] = useState([]);
  const selectAllCheckboxRef = useRef(null);
  const selectAllVisibleInDcrCheckboxRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchAllStates();
    fetchAllDivisions();
  }, []);

  const fetchAllStates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/api/all-state`, {
        headers: getAuthHeaders(),
      });
      setStates(response.data);
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to fetch states");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedStates([...states]);
    } else {
      setSelectedStates([]);
    }
  };

  const handleStateSelection = (state, checked) => {
    if (checked) {
      setSelectedStates([...selectedStates, state]);
    } else {
      setSelectedStates(selectedStates.filter((s) => s.id !== state.id));
      setSelectAll(false);
    }
  };

  const isStateSelected = (state) => {
    return selectedStates.some((s) => s.id === state.id);
  };

  const handleSelectAllDelete = (checked) => {
    if (checked) {
      setSelectedToDelete(
        priceHistoryData.map((item) => ({
          division_id: item.division_id,
          product_id: item.product_id,
          state_id: item.state_id,
          start_date: item.effective_from,
          end_date: item.state_end_date,
          product_price_id: item.product_price_id,
        }))
      );
    } else {
      setSelectedToDelete([]);
    }
  };

  // Handle individual selection for deletion
  const handleSelectDelete = (item, checked) => {
    if (checked) {
      setSelectedToDelete([
        ...selectedToDelete,
        {
          division_id: item.division_id,
          product_id: item.product_id,
          state_id: item.state_id,
          start_date: item.effective_from,
          end_date: item.state_end_date,
          product_price_id: item.product_price_id,
        },
      ]);
    } else {
      setSelectedToDelete(
        selectedToDelete.filter(
          (selected) =>
            selected.division_id !== item.division_id ||
            selected.product_id !== item.product_id ||
            selected.state_id !== item.state_id ||
            selected.start_date !== item.effective_from
        )
      );
    }
  };

  const isSelectedForDelete = (item) => {
    const isSelected = selectedToDelete.some(
      (selected) =>
        selected.division_id === item.division_id &&
        selected.product_id === item.product_id &&
        selected.state_id === item.state_id &&
        selected.start_date === item.effective_from &&
        selected.product_price_id === item.product_price_id
    );

    // optional debug
    // console.log("Checking item:", { ...item, isSelected });

    return isSelected;
  };

  const handleBatchDelete = async () => {
    if (selectedToDelete.length === 0) {
      toast.error("Please select at least one item to delete");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedToDelete.length} price list(s)?`
      )
    ) {
      try {
        setLoading(true);

        const uniquePayload = {
          product_ids: [
            ...new Set(
              selectedToDelete.map((item) => parseInt(item.product_id))
            ),
          ],
          division_id: [
            ...new Set(
              selectedToDelete.map((item) => parseInt(item.division_id))
            ),
          ],
          state_ids: [
            ...new Set(selectedToDelete.map((item) => parseInt(item.state_id))),
          ],
          product_price_id: [
            ...new Set(
              selectedToDelete
                .map((item) => parseInt(item.product_price_id))
                .filter((id) => id)
            ),
          ],
          start_date: selectedToDelete[0]?.start_date || "",
        };

        await axios.delete(`${BASE_URL}/api/product/delete-assign-states`, {
          data: uniquePayload,
          headers: getAuthHeaders(),
        });

        toast.success(
          `${selectedToDelete.length} price list(s) deleted successfully`
        );
        setSelectedToDelete([]);
        fetchPriceHistory();
      } catch (error) {
        console.error("Error deleting price lists:", error);
        toast.error("Failed to delete price lists");
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchAllDivisions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/api/divisions`, {
        headers: getAuthHeaders(),
      });
      setDivisions(response.data);
      if (response.data.length > 0 && !filterDivision) {
        setFilterDivision(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching divisions:", error);
      toast.error("Failed to fetch divisions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetch(`${BASE_URL}/api/states`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => setStates(data || []))
      .catch((err) => console.error(err));
  }, []);

  const handleApplyFilters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/api/product/?division_id=${filterDivision}&start_date=${filterDate}`,
        { headers: getAuthHeaders() }
      );

      const products = Array.isArray(response.data) ? response.data : [];

      const seenProducts = new Set();
      const uniqueProducts = products.filter((product) => {
        const key = `${product.id}-${product.mapping_code}`;
        if (seenProducts.has(key)) {
          return false;
        }
        seenProducts.add(key);
        return true;
      });

      const transformedProducts = uniqueProducts.map((product) => {
        const price = product.prices?.find(
          (p) => p.division_id === parseInt(filterDivision)
        );
        return {
          id: product.id,
          code: product.mapping_code || product.id.toString(),
          name: product.name,
          pack: product.packaging,
          mrp: price?.mrp || "",
          ptd: price?.ptd || "",
          pts: price?.s_rate || "",
          ptr: price?.r_rate || "",
          gst_rate: price?.gst_rate || "",
          c_slot: price?.c_slot || "",
          // normalize to single property used by UI
          visible_in_dcr: resolveVisibleInDcr(product, price),
          product_price_id: price?.id ?? null,
        };
      });

      setPriceListData(transformedProducts);
      toast.success("Products loaded successfully");
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterDivision) {
      handleApplyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDivision]);

  const handleEditPriceListItem = (e, field, itemId) => {
    const updatedData = priceListData.map((item) =>
      item.id === itemId ? { ...item, [field]: e.target.value } : item
    );
    setPriceListData(updatedData);
  };

  const handleVisibleInDcrChange = (e, itemId) => {
    const isChecked = e.target.checked;
    const updatedData = priceListData.map((item) =>
      item.id === itemId ? { ...item, visible_in_dcr: isChecked } : item
    );
    setPriceListData(updatedData);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const stateIds = selectedStates.map((s) => s.id);

      if (editingPriceId) {
        // update flow - send per-product array with visible_in_dcr
        const payload = {
          products: priceListData.map((product) => ({
            product_id: product.id,
            division_id: parseInt(filterDivision),
            state_ids: stateIds,
            effective_from: effectiveDate || filterDate,
            mrp: parseFloat(product.mrp || 0),
            s_rate: parseFloat(product.pts || 0),
            r_rate: parseFloat(product.ptr || 0),
            gst_rate: parseFloat(product.gst_rate || 0),
            c_slot: parseInt(product.c_slot || 0),
            ptd: parseFloat(product.ptd || 0),
            visible_in_dcr: !!product.visible_in_dcr,
            product_price_id: product.product_price_id || null,
          })),
        };

        await axios.put(
          `${BASE_URL}/api/product/product-prices-update-for-states`,
          payload,
          { headers: getAuthHeaders() }
        );

        toast.success("Price updated successfully");
      } else {
        // create flow - send per-product array with visible_in_dcr
        const payload = {
          product_ids: priceListData.map((p) => p.id),
          division_id: parseInt(filterDivision),
          state_ids: stateIds,
          visible_in_dcr: true,
          start_date: filterDate,
        };

        await axios.post(`${BASE_URL}/api/product/assign-states`, payload, {
          headers: getAuthHeaders(),
        });

        toast.success("Price list created successfully");
      }

      setEditingPriceId(null);
      setSearchParams({});
      setPriceListData([]);
      setSelectedStates([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Error submitting price list:", error);
      toast.error("Failed to submit price list");
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceHistory = async () => {
    try {
      setLoading(true);

      // Build query params safely
      const params = new URLSearchParams({
        division_id: filterDivision,
        is_active: "true",
      });

      // Pass state_id ONLY if selected
      if (selectedState) {
        params.append("state_id", selectedState);
      }

      const response = await axios.get(
        `${BASE_URL}/api/product/list-products/?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const products = Array.isArray(response.data) ? response.data : [];

      const historyData = products.flatMap((product) =>
        (product.states_details || []).map((stateDetail) => ({
          division_id: stateDetail.division_id || filterDivision,
          division_name:
            divisions.find(
              (d) =>
                d.id === (stateDetail.division_id || parseInt(filterDivision))
            )?.name || `Division ${stateDetail.division_id || filterDivision}`,
          state_id: stateDetail.state_id,
          state_name: stateDetail.state_name,
          state_end_date: stateDetail.end_date || "",
          effective_from: stateDetail.start_date || "",
          product_id: stateDetail.product_id || product.id,
          product_name: stateDetail.product_name || product.name,
          product_price_id: stateDetail.product_price_id,
          mrp: stateDetail.mrp ?? product.mrp ?? 0,
          ptd: stateDetail.ptd ?? product.ptd ?? 0,
          pts: stateDetail.pts ?? product.pts ?? 0,
          ptr: stateDetail.ptr ?? product.ptr ?? 0,
          gst_rate: stateDetail.gst_rate ?? product.gst_rate ?? 0,
          c_slot: stateDetail.c_slot ?? product.c_slot ?? 0,
          visible_in_dcr:
            !!stateDetail.visible_in_dcr || !!product.visible_in_dcr,
        }))
      );

      setPriceHistoryData(historyData);
    } catch (error) {
      console.error("Error fetching price history:", error);
      toast.error("Failed to fetch price history");
    } finally {
      setLoading(false);
    }
  };

  const handleViewEdit = async (
    divisionId,
    effectiveFrom,
    stateId,
    productPriceId
  ) => {
    try {
      setLoading(true);

      // Build the API URL with proper parameters
      const params = new URLSearchParams({
        division_id: divisionId,
        start_date: effectiveFrom,
      });

      // Only add product_price_id if it exists and is valid
      if (
        productPriceId &&
        productPriceId !== "undefined" &&
        productPriceId !== "null"
      ) {
        params.append("product_price_id", productPriceId);
      }

      const response = await axios.get(
        `${BASE_URL}/api/product/?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      // console.log("API Response:", response.data); // Debug log

      const products = Array.isArray(response.data) ? response.data : [];

      if (products.length > 0) {
        setEffectiveDate(effectiveFrom);
        setFilterDate(effectiveFrom);

        // Transform products data
        const transformedProducts = products.map((product) => {
          // Find the price that matches the criteria
          const price = product.prices?.find((p) => {
            const matchesDivision = p.division_id === parseInt(divisionId);
            const matchesDate =
              p.effective_from === effectiveFrom ||
              new Date(p.effective_from).toISOString().split("T")[0] ===
                effectiveFrom;
            const matchesPriceId =
              !productPriceId || p.id === parseInt(productPriceId);

            return matchesDivision && matchesDate && matchesPriceId;
          });

          // Use the first price as fallback if no exact match found
          const fallbackPrice =
            product.prices?.find(
              (p) => p.division_id === parseInt(divisionId)
            ) || product.prices?.[0];

          const selectedPrice = price || fallbackPrice;

          // resolve visible flag using helper
          const visibleFlag = resolveVisibleInDcr(product, selectedPrice);

          return {
            id: product.id,
            code: product.mapping_code || product.id.toString(),
            name: product.name,
            pack: product.packaging,
            mrp: selectedPrice?.mrp || "",
            ptd: selectedPrice?.ptd || "",
            pts: selectedPrice?.s_rate || "",
            ptr: selectedPrice?.r_rate || "",
            gst_rate: selectedPrice?.gst_rate || "",
            c_slot: selectedPrice?.c_slot || "",
            visible_in_dcr: visibleFlag,
            product_price_id: selectedPrice?.id, // Store the price ID for reference
          };
        });

        setPriceListData(transformedProducts);

        // Extract assigned states from the first product
        const assignedStates = [];
        const targetProduct = products[0];

        if (
          targetProduct.states_details &&
          targetProduct.states_details.length > 0
        ) {
          targetProduct.states_details.forEach((stateDetail) => {
            const stateIdToMatch = stateDetail.state_id || stateDetail.id;
            const matchedState = states.find(
              (s) => s.id === parseInt(stateIdToMatch)
            );
            if (matchedState) {
              assignedStates.push(matchedState);
            }
          });
        }

        // Fallback to state_names if states_details not available
        if (assignedStates.length === 0 && targetProduct.state_names) {
          targetProduct.state_names.forEach((stateName) => {
            const matchedState = states.find((s) => s.name === stateName);
            if (matchedState) {
              assignedStates.push(matchedState);
            }
          });
        }

        // Fallback to provided stateId
        if (assignedStates.length === 0 && stateId) {
          const matchedState = states.find((s) => s.id === parseInt(stateId));
          if (matchedState) {
            assignedStates.push(matchedState);
          }
        }

        setSelectedStates(assignedStates);
        setSelectAll(assignedStates.length === states.length);
        setFilterDivision(divisionId);
        setViewMode("view");

        // Set editing price ID correctly
        const firstPriceId = transformedProducts[0]?.product_price_id;
        setEditingPriceId(firstPriceId || productPriceId);

        toast.success("Product data loaded for editing");
      } else {
        toast.error("No products found for the selected criteria");
      }
    } catch (error) {
      console.error("Error loading product data:", error);
      console.error("Error response:", error.response?.data);
      toast.error("Failed to load product data");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectAll(false);
    setSelectedStates([]);
    setEditingPriceId(null);
    setSearchParams({});
    setPriceListData([]);
    setEffectiveDate("");
    setSelectedToDelete([]);
  };

  useEffect(() => {
    if (viewMode === "edit" && filterDivision) {
      fetchPriceHistory();
    }
  }, [viewMode, filterDivision]);

  const allVisibleInDcrSelected = () => {
    return (
      priceListData.length > 0 &&
      priceListData.every((item) => item.visible_in_dcr === true)
    );
  };

  const areSomeVisibleInDcrSelected = () => {
    return (
      priceListData.length > 0 &&
      priceListData.some((item) => item.visible_in_dcr === true) &&
      !allVisibleInDcrSelected()
    );
  };

  const handleSelectAllVisibleInDcr = (e) => {
    const isChecked = e.target.checked;

    const updatedData = priceListData.map((item) => ({
      ...item,
      visible_in_dcr: isChecked,
    }));

    setPriceListData(updatedData);
  };

  useEffect(() => {
    if (selectAllVisibleInDcrCheckboxRef.current) {
      selectAllVisibleInDcrCheckboxRef.current.indeterminate =
        areSomeVisibleInDcrSelected();
    }
  }, [priceListData]);

  return (
    <div className="flex flex-col bg-gray-50">
      <main className="flex-grow px-4 py-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">
          {viewMode === "view" && !editingPriceId
            ? "Product Price"
            : "Edit Product Price"}
          {editingPriceId && " (Editing)"}
        </h1>

        {loading && (
          <div className="mb-4 p-3 bg-blue-100 text-blue-700 rounded-md">
            Loading...
          </div>
        )}

        <div className="flex mb-6 border-b border-gray-300">
          <div className="flex mb-6 border-b border-gray-300">
            <button
              className={`py-3 px-6 rounded-none rounded-t-lg font-semibold text-sm transition-all duration-200 ${
                viewMode === "view"
                  ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-b-2 border-blue-500 shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-b-2 border-transparent"
              }`}
              onClick={() => {
                setViewMode("view");
                setEditingPriceId(null);
                setPriceListData([]);
                setEffectiveDate("");
                setSelectedToDelete([]);
              }}
            >
              {editingPriceId ? "Edit Price" : "Add Price"}
            </button>
            <button
              className={`py-3 px-6 rounded-none rounded-t-lg font-semibold text-sm transition-all duration-200 ${
                viewMode === "edit"
                  ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-b-2 border-blue-500 shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-b-2 border-transparent"
              }`}
              onClick={() => {
                setViewMode("edit");
                setEditingPriceId(null);
                setPriceListData([]);
                setEffectiveDate("");
                setSelectedToDelete([]);
                fetchPriceHistory();
              }}
            >
              Price History
            </button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md mb-6 border border-gray-300">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {viewMode === "edit" && (
              <div className="flex flex-col w-64 sm:w-64">
                <label className="mb-1 text-center">State</label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                >
                  <option value="">All</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Division
              </label>
              <select
                className="w-full p-2 border border-gray-300 rounded"
                value={filterDivision}
                onChange={(e) => setFilterDivision(e.target.value)}
                disabled={loading}
              >
                <option value="">Select Division</option>
                {divisions.map((division) => (
                  <option key={division.id} value={division.id}>
                    {division.name}
                  </option>
                ))}
              </select>
            </div>
            {viewMode === "view" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  className="w-full p-2 border border-gray-300 rounded"
                  value={effectiveDate || filterDate}
                  onChange={(e) => {
                    setFilterDate(e.target.value);
                    setEffectiveDate(e.target.value);
                  }}
                  disabled={loading}
                />
              </div>
            )}
            <div className="flex items-end">
              <CustomButton
                variant="save"
                onClick={
                  viewMode === "view" ? handleApplyFilters : fetchPriceHistory
                }
                disabled={loading || !filterDivision}
              >
                Go
              </CustomButton>
            </div>
          </div>
        </div>

        {viewMode === "view" ? (
          <>
            <p className="text-sm text-gray-600 mb-6">
              Note: Set FromDate since when prices are applicable from and Then
              Price changing date
            </p>

            {priceListData.length > 0 ? (
              <>
                <div className="border rounded shadow  table-wrapper">
                  <table className="unlock-dcr-leave-table text-sm ">
                    <thead className="bg-blue-500 text-white">
                      <tr>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          S.No
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          Code
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          Product
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          Pack
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          MRP
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          PTD
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          PTS
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          PTR
                        </th>

                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          <div className="flex items-center justify-center">
                            <span className="mr-2">Visible in DCR</span>
                            <input
                              type="checkbox"
                              ref={selectAllVisibleInDcrCheckboxRef}
                              className="  bg-gray-100 border-gray-300 rounded"
                              checked={allVisibleInDcrSelected()}
                              onChange={handleSelectAllVisibleInDcr}
                              disabled={loading}
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {priceListData.map((item, index) => (
                        <tr
                          key={item.id || index}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            {item.code}
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            {item.name}
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            {item.pack}
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center">
                            <input
                              type="number"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={item.mrp || 0}
                              onChange={(e) =>
                                handleEditPriceListItem(e, "mrp", item.id)
                              }
                              disabled={loading}
                            />
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center">
                            <input
                              type="number"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={item.ptd || 0}
                              onChange={(e) =>
                                handleEditPriceListItem(e, "ptd", item.id)
                              }
                              disabled={loading}
                            />
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center">
                            <input
                              type="number"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={item.pts || 0}
                              onChange={(e) =>
                                handleEditPriceListItem(e, "pts", item.id)
                              }
                              disabled={loading}
                            />
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center">
                            <input
                              type="number"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={item.ptr || 0}
                              onChange={(e) =>
                                handleEditPriceListItem(e, "ptr", item.id)
                              }
                              disabled={loading}
                            />
                          </td>

                          {/* Visible in DCR - Toggle */}
                          <td className="px-4 py-4 border border-gray-300 text-center">
                            <input
                              type="checkbox"
                              className="w-4 h-4  bg-gray-100 border-gray-300 rounded "
                              checked={item.visible_in_dcr || false}
                              onChange={(e) =>
                                handleVisibleInDcrChange(e, item.id)
                              }
                              disabled={loading}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-lg mt-6 border-2 border-gray-200">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b border-gray-200 pb-2">
                    Select States
                  </h3>

                  <div className="mb-4">
                    <label className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-5 h-5 border-2 border-gray-300 rounded"
                        disabled={loading}
                      />
                      <span className="text-sm font-semibold text-gray-700">
                        Select All States
                      </span>
                    </label>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
                    {states.map((state, index) => (
                      <label
                        key={index}
                        className="flex items-center space-x-2 p-2 rounded-md hover:bg-white transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isStateSelected(state)}
                          onChange={(e) =>
                            handleStateSelection(state, e.target.checked)
                          }
                          className="w-4 h-4 border-2 border-gray-300 rounded"
                          disabled={loading}
                        />
                        <span className="text-sm text-gray-700 font-medium">
                          {state.name}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div className="flex space-x-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSubmit}
                      disabled={loading || selectedStates.length === 0}
                      className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editingPriceId ? "Update" : "Submit"}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={loading}
                      className="px-8 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg shadow-md hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <p className="text-gray-500">
                  No products found. Select a division and click "Go" to load
                  products.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            {priceHistoryData.length > 0 ? (
              <div className="border rounded shadow  table-wrapper">
                <table className="unlock-dcr-leave-table text-sm ">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      <th className="px-4 py-4 border border-gray-300 text-center">
                        <div className="flex flex-col items-center space-y-2">
                          <CustomButton
                            variant="delete"
                            onClick={handleBatchDelete}
                            disabled={loading || selectedToDelete.length === 0}
                          >
                            Remove
                          </CustomButton>

                          <input
                            type="checkbox"
                            className="w-4 h-4 bg-gray-100 border-gray-300 rounded"
                            checked={
                              selectedToDelete.length ===
                                priceHistoryData.length &&
                              priceHistoryData.length > 0
                            }
                            onChange={(e) =>
                              handleSelectAllDelete(e.target.checked)
                            }
                            disabled={loading || priceHistoryData.length === 0}
                          />
                        </div>
                      </th>

                      <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                        S.No
                      </th>
                      <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                        Product ID
                      </th>
                      <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                        Division
                      </th>
                      <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                        Product
                      </th>
                      <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                        State
                      </th>
                      <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                        Start Date
                      </th>
                      <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                        End Date
                      </th>
                      <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {priceHistoryData.map((item, index) => (
                      <tr
                        key={`${item.division_id}-${item.state_id}-${item.effective_from}-${item.product_price_id}-${index}`}
                        className={
                          index % 2 === 0
                            ? "bg-white hover:bg-gray-50"
                            : "bg-gray-50 hover:bg-gray-100"
                        }
                      >
                        <td className="px-4 py-4 border border-gray-300 text-center">
                          <input
                            type="checkbox"
                            className="w-4 h-4 bg-gray-100 border-gray-300 rounded"
                            checked={isSelectedForDelete(item)}
                            onChange={(e) =>
                              handleSelectDelete(item, e.target.checked)
                            }
                            disabled={loading}
                          />
                        </td>
                        <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                          {item.product_id || "-"}
                        </td>
                        <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                          {item.division_name}
                        </td>
                        <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                          {item.product_name}
                        </td>
                        <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                          {item.state_name}
                        </td>
                        <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                          {item.effective_from}
                        </td>
                        <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                          {item.state_end_date || "-"}
                        </td>
                        <td className="px-4 py-4 border border-gray-300 text-center">
                          <div className="flex justify-center space-x-3">
                            <CustomButton
                              variant="save"
                              className="text-xs font-semibold rounded-md shadow-sm disabled:opacity-50"
                              onClick={() =>
                                handleViewEdit(
                                  item.division_id,
                                  item.effective_from,
                                  item.state_id,
                                  item.product_price_id
                                )
                              }
                              disabled={loading}
                            >
                              View/Edit
                            </CustomButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <p className="text-gray-500">
                  No price history found. Select a division and click "Go" to
                  load price history.
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ProductPriceManager;
